<script lang="ts">
	import type { ImageBlock } from '../imageBlock.svelte';
	import type { SbColor, SbMuxVideo } from '$lib/storyblok/types';
	import PageGrid16 from '$lib/storyblok/components/pageGrid16.svelte';
	import type { ISbRichtext, ISbStoryData } from '@storyblok/js';
	import Blok from '$lib/storyblok/blok.svelte';
	import { editable } from '$lib/storyblok/editable';
	import SbRichtext from '$lib/storyblok/components/sbRichtext.svelte';

	interface Props {
		content: ISbStoryData<{
			text_alignment: 'left' | 'right';
			text_alignment_mobile: 'top' | 'bottom';
			text_content_alignment_mobile: 'left' | 'center';
			variant: 'default' | 'full-bleed' | 'image-50';
			column_start: '1' | '2';
			heading: string;
			text: string | ISbRichtext;
			asset: Array<ISbStoryData<ImageBlock | SbMuxVideo>['content']>;
			desktop_asset: Array<ISbStoryData<ImageBlock | SbMuxVideo>['content']>;
			background_color?: SbColor;
			text_color?: SbColor;
		}>['content'];
	}

	let { content }: Props = $props();

	let asset = $derived(content.asset?.[0]);
	let desktopAsset = $derived(content.desktop_asset?.[0]);

	const leftStart = `${content.column_start === '2' || content.text_alignment === 'left' ? 'lg:col-start-2' : 'lg:col-start-1'} lg:col-end-8 lg:row-start-1`;
	const rightStart = `lg:col-start-9 ${content.column_start === '2' || content.text_alignment === 'right' ? 'lg:col-end-[16]' : 'lg:col-end-[17]'} lg:row-start-1`;
</script>

{#snippet responsiveAsset(threshold?: number | null)}
	<div class="h-full" class:lg:hidden={desktopAsset}>
		<Blok {threshold} content={asset} />
	</div>
	{#if desktopAsset}
		<div class="h-full max-lg:hidden">
			<Blok {threshold} content={desktopAsset} />
		</div>
	{/if}
{/snippet}

<section
	class="relative lg:max-h-svh bg-{content.background_color}"
	use:editable={content}
	class:gap-y-12={content.variant === 'image-50'}
	class:flex={content.variant === 'image-50'}
	class:flex-col={content.variant === 'image-50' && content.text_alignment_mobile === 'top'}
	class:flex-col-reverse={content.variant === 'image-50' && content.text_alignment_mobile !== 'top'}
>
	{#if asset}
		{#if content.variant === 'full-bleed'}
			<div class="relative z-0 aspect-2/3 h-full max-h-svh w-full lg:aspect-3/2">
				{@render responsiveAsset(asset.component === 'mux_video' ? 0.5 : null)}
			</div>
		{/if}
	{/if}
	<PageGrid16
		margin
		class="{content.variant === 'full-bleed'
			? 'absolute inset-0 z-10 h-full'
			: ''} {content.variant !== 'image-50'
			? 'pb-5 lg:py-[106px]'
			: 'lg:py-[330px]'} {content.variant === 'image-50' && content.text_alignment_mobile !== 'top'
			? 'pb-16'
			: 'pt-16'}"
		gridClass="h-full"
	>
		<div
			class:row-start-2={content.variant === 'default' && content.text_alignment_mobile !== 'top'}
			class:max-lg:mb-12={content.variant === 'default' && content.text_alignment_mobile === 'top'}
			class:max-lg:mt-12={content.variant === 'default' && content.text_alignment_mobile !== 'top'}
			class:justify-center={content.variant !== 'full-bleed'}
			class:lg:justify-center={content.variant === 'full-bleed'}
			class:max-lg:text-center={content.text_content_alignment_mobile === 'center' ||
				!content.text_content_alignment_mobile}
			class:max-lg:text-left={content.text_content_alignment_mobile === 'left'}
			class="col-span-full flex flex-col gap-y-3 text-balance lg:gap-y-6 text-{content.text_color ||
				'brown-1'} {content.text_alignment === 'left' ? leftStart : rightStart}"
		>
			<h1 class="text-[36px]/[112%] tracking-[-.36px] lg:text-[64px]/[110%] lg:tracking-[-.64px]">
				{content.heading}
			</h1>
			<p
				class="side-by-side-content text-[16px]/[150%] tracking-[-.16px] lg:text-[20px]/[140%] lg:tracking-[-.2px]"
			>
				<SbRichtext text={content.text} />
			</p>
		</div>
		{#if asset && content.variant === 'default'}
			<div
				class="col-span-full aspect-square overflow-hidden rounded-2xl {content.text_alignment ===
				'left'
					? rightStart
					: leftStart}"
			>
				{@render responsiveAsset()}
			</div>
		{/if}
	</PageGrid16>

	{#if content.variant === 'image-50'}
		<div
			class="w-full max-lg:aspect-4/3 lg:absolute lg:inset-y-0 lg:w-1/2"
			class:lg:right-0={content.text_alignment === 'left'}
			class:lg:left-0={content.text_alignment !== 'left'}
		>
			{@render responsiveAsset()}
		</div>
	{/if}
</section>

<style lang="scss">
	.side-by-side-content {
		:global {
			ol,
			ul,
			p {
				&:not(:last-child) {
					margin-bottom: theme('spacing.4');

					@media (min-width: 1024px) {
						margin-bottom: theme('spacing.5');
					}
				}
			}

			ul,
			ol {
				padding-left: 20px;
			}

			li p {
				display: inline;
			}

			ul li {
				list-style-type: disc;
				&:not(:last-child) {
					margin-bottom: theme('spacing.1');

					@media (min-width: 1024px) {
						margin-bottom: theme('spacing.2');
					}
				}
			}

			ol li {
				list-style-type: decimal;
			}
		}
	}
</style>
